import {
  collection,
  doc,
  orderBy,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { deleteField, Timestamp } from "firebase/firestore";
import { OrderedMap } from "immutable";
import forEach from "lodash/forEach";
import groupBy from "lodash/groupBy";
import PropTypes from "prop-types";
import { collectionData, docData } from "rxfire/firestore";
import { Subject } from "rxjs";
import { map, tap } from "rxjs/operators";

import { firestore } from "../firebase";
import { STATE_NAMES } from "../lib/investment";
import { createdAt, updatedAt } from "../lib/timestamp";
import { store as storeAcceptedInvitationRounds } from "./accepted-investment-rounds";

export const subject = new Subject();
let data = OrderedMap();
let byInvestmentRoundSyncAt = 0;
let byProjectSyncAt = {};

export function clear() {
  data = OrderedMap();
  byInvestmentRoundSyncAt = 0;
  byProjectSyncAt = {};
}

export function getData(projectId) {
  return (data.get(projectId) || Map()).toArray().map(([, item]) => item);
}

export const collectionName = "investments";

export const propTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
});

export const store = {
  getDocRef: (id) => doc(firestore, collectionName, id),
  getById: (id, getData = true) => {
    const docRef = store.getDocRef(id);

    return getData ? docData(docRef, { idField: "id" }) : docRef;
  },
  listByInvestmentRounds: (investorId) => {
    const docRef = query(
      collection(firestore, collectionName),
      orderBy("updatedAt", "desc"),
      where("investorId", "==", investorId),
      where("updatedAt", ">", Timestamp.fromMillis(byInvestmentRoundSyncAt))
    );

    return collectionData(docRef, { idField: "id" }).pipe(
      map((items) => {
        forEach(
          groupBy(items, (i) => i.investmentRoundId),
          (investmentsPerRound, investmentRoundId) => {
            const newItems = OrderedMap(
              investmentsPerRound.map((i) => [i.id, i])
            );

            const newData = newItems.mergeWith(
              (oldVal, newVal) => oldVal || newVal,
              data.get(investmentRoundId)
            );

            data = data.set(investmentRoundId, newData);
          }
        );

        return data
          .map((investmentsPerRound) =>
            investmentsPerRound
              .toArray()
              .map(([, item]) => item)
              .filter((item) => item.investorId === investorId)
              .sort((a, b) => b.createdAt - a.createdAt)
          )
          .toJS();
      }),
      tap(() => (byInvestmentRoundSyncAt = new Date().getTime()))
    );
  },

  getByProjectIdAndInvestmentRoundId: (projectId, investmentRoundId) => {
    const docRef = query(
      collection(firestore, collectionName),
      orderBy("updatedAt", "desc"),
      where("investmentRoundId", "==", investmentRoundId),
      where("projectId", "==", projectId),
      where(
        "updatedAt",
        ">",
        Timestamp.fromMillis(byProjectSyncAt[investmentRoundId] || 0)
      )
    );

    return collectionData(docRef, { idField: "id" }).pipe(
      map((items) => {
        const newItems = OrderedMap(items.map((i) => [i.id, i]));
        const newData = newItems.mergeWith(
          (oldVal, newVal) => oldVal || newVal,
          data.get(investmentRoundId)
        );

        data = data.set(investmentRoundId, newData);

        return newData.toArray().map(([, item]) => item);
      }),
      tap(() => (byProjectSyncAt[investmentRoundId] = new Date().getTime()))
    );
  },

  listByProject: (projectId) => {
    const docRef = query(
      collection(firestore, collectionName),
      orderBy("updatedAt", "desc"),
      where("projectId", "==", projectId),
      where(
        "updatedAt",
        ">",
        Timestamp.fromMillis(byProjectSyncAt[projectId] || 0)
      )
    );

    return collectionData(docRef, { idField: "id" }).pipe(
      map((items) => {
        const newItems = OrderedMap(items.map((i) => [i.id, i]));
        const existing = data.get(projectId) || OrderedMap();
        const newData = newItems.mergeWith(
          (oldVal, newVal) => newVal || oldVal,
          existing
        );

        data = data.set(projectId, newData);

        return newData.toArray().map(([, item]) => item);
      }),
      tap(() => (byProjectSyncAt[projectId] = new Date().getTime()))
    );
  },
  update: (id, docData, original) => {
    const docRef = store.getDocRef(id);
    const batch = writeBatch(firestore);
    batch.set(docRef, { ...docData, ...updatedAt() }, { merge: true });
    if (
      original &&
      original.state != docData.state &&
      docData.state != STATE_NAMES.CLAIMED_BY_INVESTOR
    ) {
      batch.set(
        storeAcceptedInvitationRounds.getDocRef(
          original.investmentRoundId,
          original.investorId
        ),
        {
          claimedInvestment: deleteField(),
          ...updatedAt(),
        },
        { merge: true }
      );
    }
    return batch.commit();
  },

  create: (newDoc, id) => {
    const docRef = store.getDocRef(id);
    const batch = writeBatch(firestore);
    batch.set(docRef, {
      ...newDoc,
      state: STATE_NAMES.CLAIMED_BY_INVESTOR,
      ...createdAt(),
    });

    batch.set(
      storeAcceptedInvitationRounds.getDocRef(
        newDoc.investmentRoundId,
        newDoc.investorId
      ),
      { claimedInvestment: { ...newDoc, id: docRef.id }, ...updatedAt() },
      { merge: true }
    );

    return batch.commit();
  },
};
